<template>
  <div class="gs-asset-segment" role="tablist" :aria-label="label">
    <button
      v-for="item in items"
      :key="item.value"
      type="button"
      role="tab"
      class="gs-asset-segment__item"
      :class="{
        'is-active': current === item.value,
        'has-error': hasError(item),
      }"
      :aria-selected="current === item.value ? 'true' : 'false'"
      @click="$emit('select', item.value)"
    >
      {{ item.title }}
    </button>
  </div>
</template>

<script>
/**
 * Segmented control for the asset types that hold more than one collection
 * (press releases / articles, Facebook / X / LinkedIn, email / forum). It
 * takes the same `[{ title, value }]` list the tabs already kept in `data`, so
 * the sub-tab state each tab owns does not change shape.
 *
 * Selection is blue (`--gs-active`), never pink: pink stays reserved for the
 * one primary action and the current asset type in the sidebar.
 */
export default {
  name: 'AssetSegmented',

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    current: {
      type: String,
      default: '',
    },
    // Values whose collection currently has a validation error, in the same
    // shape the tabs' existing `*ErrorTabs` computeds return.
    errorValues: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Sections',
    },
  },

  methods: {
    hasError(item) {
      return this.errorValues.indexOf(item.value) !== -1;
    },
  },
};
</script>

<style scoped>
.gs-asset-segment {
  display: inline-flex;
  gap: 2px;
  margin-bottom: 20px;
  padding: 3px;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-md);
}

.gs-asset-segment__item {
  min-height: 34px;
  padding: 6px 16px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.gs-asset-segment__item:hover {
  color: var(--gs-foreground);
}

.gs-asset-segment__item.is-active {
  color: var(--gs-active);
  background: var(--gs-active-soft);
  border-color: var(--gs-active-border);
}

.gs-asset-segment__item.has-error {
  color: var(--gs-danger);
}

.gs-asset-segment__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}
</style>
